import { MessageCircle, Play } from "lucide-react";
import { useRef, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { consultWhatsappUrl, whatsappConfigured } from "@/context/CartContext";
import { sizes, formatPrice } from "@/config/products";

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function SizeGuide() {
  const { t } = useLanguage();
  const waOn = whatsappConfigured();
  const maxPrice = Math.max(...sizes.map((s) => s.price));

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState("");

  const playVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.controls = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    setIsPlaying(true);
  };

  return (
    <section id="size-guide" data-testid="size-guide-section" className="bg-[#f7f7f5] py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-5 lg:px-10">
        <Reveal className="mb-12 text-center">
          <span className="eyebrow text-[11px] text-[#999]">{t.nav.sizeGuide}</span>
          <h2 className="mt-3 font-serif-display text-3xl font-medium text-[#111] sm:text-4xl">{t.sizeGuide.title}</h2>
          <p className="mx-auto mt-3 max-w-md font-body text-sm text-[#777]">{t.sizeGuide.subtitle}</p>
        </Reveal>

        <div
          data-testid="size-guide-video"
          className="group relative mx-auto mb-14 max-w-3xl overflow-hidden rounded-2xl border border-[#eee] bg-white shadow-[0_2px_24px_rgba(0,0,0,0.05)]"
        >
          <div className="relative aspect-video w-full bg-[#0a0a0a]">
              <video
                ref={videoRef}
                src="/videos/macro-secondary.mp4#t=0.1"
                poster="/videos/moissanite-hero-poster.jpg"
                preload="metadata"
                playsInline
                muted
                onLoadedMetadata={(e) => {
                  setVideoDuration(formatDuration(e.currentTarget.duration));
                  try { if (e.currentTarget.currentTime === 0) e.currentTarget.currentTime = 0.05; } catch (_) {}
                }}
                onEnded={() => {
                  const v = videoRef.current;
                  if (v) { v.controls = false; v.muted = true; try { v.currentTime = 0.05; } catch (_) {} }
                  setIsPlaying(false);
                }}
                className="h-full w-full object-cover"
              />
              {!isPlaying && (
                <button
                  type="button"
                  onClick={playVideo}
                  aria-label={t.sizeGuide.videoAria}
                  data-testid="size-guide-video-play"
                  className="absolute inset-0 flex items-center justify-center focus:outline-none"
                >
                  <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/25" />
                  <span
                    data-testid="size-guide-video-badge"
                    className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 font-body text-[11px] uppercase tracking-[0.18em] text-white backdrop-blur-md"
                  >
                    <Play className="h-3 w-3 fill-white" />
                    <span>{t.sizeGuide.videoLabel}</span>
                    {videoDuration && (<><span className="opacity-60">•</span><span className="tracking-normal">{videoDuration}</span></>)}
                  </span>
                  <span
                    aria-hidden="true"
                    className="video-play-breath relative flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-[0_10px_40px_rgba(0,0,0,0.35)] transition-transform duration-500 group-hover:scale-[1.06]"
                  >
                    <Play className="h-6 w-6 translate-x-[1px] fill-[#111] text-[#111]" />
                  </span>
                </button>
              )}
            </div>
          </div>

        <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
          {sizes.map((s, i) => {
            const scale = 0.5 + (s.price / maxPrice) * 0.5; // círculo proporcional
            return (
              <Reveal key={s.id} delay={i * 0.08}>
                <div
                  data-testid={`size-card-${s.id}`}
                  className="flex h-full flex-col items-center rounded-2xl border border-[#eee] bg-white p-6 text-center"
                >
                  <div className="flex h-24 items-center justify-center">
                    <span
                      className="rounded-full bg-gradient-to-br from-[#e9e9e6] to-[#cfcfca] ring-1 ring-[#dcdcd6]"
                      style={{ width: `${scale * 72}px`, height: `${scale * 72}px` }}
                    />
                  </div>
                  <p className="mt-2 font-serif-display text-2xl text-[#111]">{s.label}</p>
                  <p className="mt-1 font-body text-xs text-[#999]">
                    {t.sizeGuide.perEarring}: {s.perEarring}
                  </p>
                  <p className="mt-3 font-body text-sm text-[#555]">{t.sizeGuide.styles[s.id]}</p>
                  <span className="mt-4 font-serif-display text-xl text-[#111]">{formatPrice(s.price)}</span>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-8 text-center">
          <p className="mx-auto max-w-xl font-body text-xs leading-relaxed text-[#999]">{t.sizeGuide.note}</p>
          {waOn && (
            <a
              data-testid="size-guide-help"
              href={consultWhatsappUrl(t)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#ddd] px-6 py-3 font-body text-sm text-[#111] transition-colors duration-200 hover:border-[#111]"
            >
              <MessageCircle className="h-4 w-4" /> {t.sizeGuide.help}
            </a>
          )}
        </Reveal>
      </div>
    </section>
  );
}
